import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { EditOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useCreateSimEventMutation, useListSimEventsQuery } from '../../api/simulationApi';
import type { SimEvent, StateCorrectionMetric } from '../../types/simulation';

const { Text, Paragraph } = Typography;

/**
 * Коррекции показателей (backend docs/RFC_STATE_CORRECTIONS.md): событие
 * `state_correction` — «на день D значение было X». Точка на графике за D
 * равна введённому числу, D+1 считается от него; дата ≤ курсора — ретро
 * (энергия, гейт реального режима — как у полива). Температура почвы —
 * замер: расчёт частично подтягивается к нему и без новых замеров затухает.
 */

export type CorrectionTargetKind = 'planting' | 'bed';

export interface CorrectionTarget {
  kind: CorrectionTargetKind;
  id: string;
  label: string;
}

interface MetricMeta {
  code: StateCorrectionMetric;
  target: CorrectionTargetKind;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  hint?: string;
}

export const CORRECTION_METRICS: MetricMeta[] = [
  { code: 'health_pct', target: 'planting', label: 'Здоровье', unit: '%', min: 0, max: 100, step: 1 },
  { code: 'height_cm', target: 'planting', label: 'Высота', unit: 'см', min: 0, max: 1000, step: 1 },
  { code: 'soil_moisture_pct', target: 'bed', label: 'Влага почвы', unit: '%', min: 0, max: 100, step: 1,
    hint: 'Влага — свойство грядки: правка затронет все посадки на ней.' },
  { code: 'soil_temp_c', target: 'bed', label: 'Температура почвы (замер)', unit: '°C', min: -30, max: 60, step: 0.5,
    hint: 'Замер частично корректирует расчёт температуры почвы. Без новых замеров его влияние постепенно ослабевает.' },
  { code: 'soil_ph', target: 'bed', label: 'Кислотность', unit: 'pH', min: 3, max: 10, step: 0.1 },
  { code: 'soil_nitrogen', target: 'bed', label: 'Азот', unit: 'индекс 0–100', min: 0, max: 100, step: 1 },
  { code: 'soil_phosphorus', target: 'bed', label: 'Фосфор', unit: 'индекс 0–100', min: 0, max: 100, step: 1 },
  { code: 'soil_potassium', target: 'bed', label: 'Калий', unit: 'индекс 0–100', min: 0, max: 100, step: 1 },
];

const metricLabel = (code: string): string => CORRECTION_METRICS.find((m) => m.code === code)?.label ?? code;
const metricUnit = (code: string): string => CORRECTION_METRICS.find((m) => m.code === code)?.unit ?? '';

interface FormValues {
  targetKey: string;
  metric: StateCorrectionMetric;
  value?: number;
  date: Dayjs;
  notes?: string;
}

interface StateCorrectionsPanelProps {
  saveId: string;
  sessionId?: string | null;
  /** Все цели, которые можно править с этой страницы. */
  targets: CorrectionTarget[];
  /** Дата курсора сейва: подставляется в форму по умолчанию. */
  cursorDate?: string | null;
  /**
   * Фильтры списка коррекций (до двух: посадка и её грядка на странице посадки);
   * без фильтров — все события сейва.
   */
  listFilters?: Array<{ plantingId?: string; bedId?: string }>;
  title?: string;
}

const targetKey = (t: CorrectionTarget) => `${t.kind}:${t.id}`;

const eventCorrections = (ev: SimEvent): Record<string, number> => {
  const payload = (ev as unknown as { payload?: { corrections?: Record<string, number> } }).payload;
  return payload?.corrections && typeof payload.corrections === 'object' ? payload.corrections : {};
};

export const StateCorrectionsPanel: React.FC<StateCorrectionsPanelProps> = ({
  saveId, sessionId, targets, cursorDate, listFilters, title = 'Коррекции показателей',
}) => {
  const [form] = Form.useForm<FormValues>();
  const [showForm, setShowForm] = useState(false);
  const [createSimEvent, { isLoading: creating }] = useCreateSimEventMutation();

  // Бэкенд фильтрует по одной цели за запрос: посадка и её грядка — два запроса.
  const primaryFilter = listFilters?.[0] ?? {};
  const secondaryFilter = listFilters?.[1] ?? null;
  const primary = useListSimEventsQuery(
    { saveId, sessionId: sessionId || undefined, ...primaryFilter },
    { skip: !saveId },
  );
  const secondary = useListSimEventsQuery(
    { saveId, sessionId: sessionId || undefined, ...(secondaryFilter || {}) },
    { skip: !saveId || !secondaryFilter },
  );
  const isLoading = primary.isLoading || secondary.isLoading;
  const isError = primary.isError || secondary.isError;
  const corrections = useMemo(() => {
    const seen = new Set<string>();
    const merged: SimEvent[] = [];
    for (const ev of [...(primary.data?.events || []), ...(secondary.data?.events || [])]) {
      if (ev.action !== 'state_correction' || seen.has(ev.id)) continue;
      seen.add(ev.id);
      merged.push(ev);
    }
    return merged.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  }, [primary.data, secondary.data]);

  const selectedTargetKey = Form.useWatch('targetKey', form);
  const selectedTarget = targets.find((t) => targetKey(t) === selectedTargetKey) || targets[0] || null;
  const selectedMetric = Form.useWatch('metric', form);
  const metricsForTarget = CORRECTION_METRICS.filter((m) => m.target === (selectedTarget?.kind ?? 'planting'));
  const metricMeta = metricsForTarget.find((m) => m.code === selectedMetric) || metricsForTarget[0];

  const handleTargetChange = (key: string) => {
    const t = targets.find((x) => targetKey(x) === key);
    const first = CORRECTION_METRICS.find((m) => m.target === (t?.kind ?? 'planting'));
    form.setFieldsValue({ metric: first?.code, value: undefined });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const target = targets.find((t) => targetKey(t) === values.targetKey);
      const meta = CORRECTION_METRICS.find((m) => m.code === values.metric);
      if (!target || !meta) return;
      const value = Number(values.value);
      if (!Number.isFinite(value)) {
        message.error('Укажите числовое значение');
        return;
      }
      await createSimEvent({
        saveId,
        sessionId: sessionId || undefined,
        action: 'state_correction',
        date: values.date.format('YYYY-MM-DD'),
        ...(target.kind === 'planting' ? { plantingId: target.id } : { bedId: target.id }),
        corrections: { [meta.code]: value },
        notes: values.notes?.trim() || undefined,
      }).unwrap();
      message.success('Коррекция записана');
      form.resetFields(['value', 'notes']);
      setShowForm(false);
    } catch (err) {
      const e = err as { data?: { message?: string; errorCode?: string }; errorFields?: unknown; status?: number };
      if (e?.errorFields) return;
      const msg = e?.data?.message;
      if (e.status === 402) message.error(msg || 'Не хватает энергии на пересчёт истории');
      else if (e.status === 403) message.error(msg || 'Правка прошлого недоступна в этом режиме');
      else if (e.status === 429) message.error(msg || 'Слишком много коррекций за день');
      else message.error(msg || 'Не удалось записать коррекцию');
    }
  };

  const targetLabel = (ev: SimEvent): string => {
    const id = ev.plantingId || ev.planting_id || ev.bedId || ev.bed_id || null;
    const t = targets.find((x) => x.id === id);
    return t ? t.label : id ? `${id.slice(0, 8)}…` : '—';
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <EditOutlined /> {title}
          <Tooltip title="Введённое значение становится итогом выбранного дня; следующий день считается от него. Правка дня до курсора пересчитывает историю и оплачивается энергией, как полив задним числом.">
            <InfoCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      }
      extra={
        <Button
          size="small"
          type={showForm ? 'default' : 'primary'}
          icon={<PlusOutlined />}
          disabled={targets.length === 0}
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? 'Скрыть форму' : 'Исправить'}
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      {targets.length === 0 ? (
        <Alert type="info" showIcon message="Нет грядок и посадок, которые можно править" style={{ marginBottom: 8 }} />
      ) : null}

      {showForm && targets.length > 0 ? (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            targetKey: targetKey(targets[0]),
            metric: CORRECTION_METRICS.find((m) => m.target === targets[0].kind)?.code,
            date: cursorDate ? dayjs(cursorDate) : dayjs(),
          }}
          style={{ marginBottom: 16 }}
        >
          <Space wrap align="start" style={{ width: '100%' }}>
            <Form.Item name="targetKey" label="Что правим" rules={[{ required: true }]} style={{ minWidth: 220 }}>
              <Select
                options={targets.map((t) => ({ value: targetKey(t), label: `${t.kind === 'bed' ? 'Грядка' : 'Посадка'} · ${t.label}` }))}
                onChange={handleTargetChange}
              />
            </Form.Item>
            <Form.Item name="metric" label="Показатель" rules={[{ required: true }]} style={{ minWidth: 220 }}>
              <Select options={metricsForTarget.map((m) => ({ value: m.code, label: `${m.label} (${m.unit})` }))} />
            </Form.Item>
            <Form.Item
              name="value"
              label={`Значение за день${metricMeta ? ` (${metricMeta.unit})` : ''}`}
              rules={[{ required: true, message: 'Укажите значение' }]}
              style={{ minWidth: 160 }}
            >
              <InputNumber style={{ width: '100%' }} min={metricMeta?.min} max={metricMeta?.max} step={metricMeta?.step ?? 1} />
            </Form.Item>
            <Form.Item
              name="date"
              label="День"
              rules={[{ required: true }]}
              style={{ minWidth: 160 }}
              extra={cursorDate ? 'Прошлые дни закрыты для правки' : undefined}
            >
              {/* Коррекция раньше курсора отвергается сервером (STATE_CORRECTION_PAST_DATE):
                  пересчёт прошлого переигрывает уже исполненные события. */}
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={(d) => (cursorDate ? d.isBefore(dayjs(cursorDate), 'day') : false)}
              />
            </Form.Item>
            <Form.Item name="notes" label="Заметка" style={{ minWidth: 220 }}>
              <Input placeholder="осмотр, замер датчиком…" maxLength={200} />
            </Form.Item>
          </Space>
          {metricMeta?.hint ? (
            <Alert type="info" showIcon message={metricMeta.hint} style={{ marginBottom: 12 }} />
          ) : null}
          <Space>
            <Button type="primary" loading={creating} onClick={handleSubmit}>
              Записать
            </Button>
            <Button onClick={() => setShowForm(false)}>Отмена</Button>
          </Space>
        </Form>
      ) : null}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin />
        </div>
      ) : isError ? (
        <Alert type="error" showIcon message="Не удалось загрузить коррекции" />
      ) : corrections.length === 0 ? (
        <Empty description="Коррекций пока нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table
          size="small"
          dataSource={corrections}
          rowKey={(r) => r.id}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          columns={[
            {
              title: 'День',
              dataIndex: 'date',
              key: 'date',
              width: 110,
              render: (v: string) => (v ? dayjs(v.slice(0, 10)).format('DD.MM.YYYY') : '—'),
            },
            { title: 'Цель', key: 'target', render: (_, ev) => targetLabel(ev) },
            {
              title: 'Показатели',
              key: 'corrections',
              render: (_, ev) => (
                <Space size={4} wrap>
                  {Object.entries(eventCorrections(ev)).map(([code, value]) => (
                    <Tag key={code}>
                      {metricLabel(code)}: <Text strong>{value}</Text> {metricUnit(code)}
                    </Tag>
                  ))}
                </Space>
              ),
            },
            {
              title: 'Заметка',
              dataIndex: 'notes',
              key: 'notes',
              render: (v: string | null) => v || <Text type="secondary">—</Text>,
            },
          ]}
        />
      )}

      <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
        Коррекция — событие на таймлайне: она переживает пересчёты и удаляется откатом курсора
        вместе с другими действиями правее него.
      </Paragraph>
    </Card>
  );
};

export default StateCorrectionsPanel;
